import { HRConversationArea } from '@/components/agent/HRConversationArea';
import {
  CallState,
  LoadingState,
  useHRAgentCall,
} from '@/hooks/useHRAgentCall';
import { useCoachStore } from '@/store/coach';
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Mock call history data with summaries
const CALL_HISTORY = [
  {
    id: 1,
    date: '2024/01/08 14:23',
    duration: '5:34',
    topic: '給与明細について',
    summary: '12月分の給与明細の確認方法について問い合わせ。社内システムからのダウンロード手順を説明し、パスワードリセットの方法も案内。問題は解決し、無事に明細を確認できた。'
  },
  {
    id: 2,
    date: '2024/01/08 11:45',
    duration: '3:21',
    topic: 'コミッション支払日',
    summary: 'Coupang Eats配達員からの問い合わせ。1月分のコミッション支払日を確認。2月15日に振込予定であることを説明。振込先口座の変更方法についても案内。'
  },
  {
    id: 3,
    date: '2024/01/08 09:12',
    duration: '7:45',
    topic: '有給休暇の申請',
    summary: '有給休暇の申請方法と残日数の確認。入社2年目で14日の有給があることを確認。申請システムの使い方を詳しく説明し、上司承認のプロセスについても案内。'
  },
  {
    id: 4,
    date: '2024/01/07 16:30',
    duration: '4:12',
    topic: '労働時間について',
    summary: '月間の残業時間上限について質問。36協定に基づく月45時間の上限を説明。現在の残業時間は32時間で問題ないことを確認。健康管理の観点からのアドバイスも提供。'
  },
  {
    id: 5,
    date: '2024/01/07 13:15',
    duration: '6:23',
    topic: '社会保険の手続き',
    summary: '扶養家族追加に伴う社会保険の変更手続きについて。必要書類（住民票、戸籍謄本）を案内し、提出期限を確認。手続き完了後の保険証発行時期についても説明。'
  },
];

// UI text configurations by company
const UI_TEXT: Record<string, {
  subtitle: string;
  startButton: string;
  availability: string;
  connecting: string;
  endCall: string;
  callEnded: string;
  thankYou: string;
  newCall: string;
  error: string;
  reconnect: string;
  errorMessage: string;
  callHistory: string;
  callTime: string;
}> = {
  coupang: {
    subtitle: '給与・コミッション・労務に関するご相談はこちら',
    startButton: '相談を開始する',
    availability: '24時間365日対応',
    connecting: '接続中...',
    endCall: '通話終了',
    callEnded: '通話が終了しました',
    thankYou: 'ご利用ありがとうございました',
    newCall: '新しい相談を開始',
    error: '接続エラーが発生しました',
    reconnect: '再接続',
    errorMessage: '申し訳ございません。技術的な問題が発生しました。',
    callHistory: '通話履歴',
    callTime: '通話時間',
  },
  grab: {
    subtitle: 'Product Training & Support',
    startButton: 'Start Training Session',
    availability: 'Available 24/7',
    connecting: 'Connecting...',
    endCall: 'End Session',
    callEnded: 'Training session completed',
    thankYou: 'Great work today!',
    newCall: 'Start New Session',
    error: 'Connection Error',
    reconnect: 'Reconnect',
    errorMessage: 'Sorry, we encountered a technical issue.',
    callHistory: 'Training History',
    callTime: 'Session Duration',
  },
};

export function HRAgent() {
  const coachStore = useCoachStore();
  const [hasStartedCall, setHasStartedCall] = useState(false);
  const [selectedCall, setSelectedCall] = useState<typeof CALL_HISTORY[0] | null>(null);
  const [searchParams] = useSearchParams();

  // Get company and persona from URL params
  const company = searchParams.get('company') || 'coupang';
  const persona = searchParams.get('persona');

  const {
    loadingState,
    error,
    callState,
    agentInfo,
    callDuration,
    transcript,
    isSpeaking,
    handleAnswerCall,
    handleEndCall,
    formatTime,
  } = useHRAgentCall({
    userId: coachStore.userId,
    persona,
    company,
    onCallEnded: () => {
      // Stay on the same page
    },
  });

  // Get UI text for current company
  const text = UI_TEXT[company] || UI_TEXT.coupang;

  const handleStartCall = () => {
    setHasStartedCall(true);
    handleAnswerCall();
  };

  const handleNewCall = () => {
    window.location.reload();
  };

  // Base layout with sidebar and main area
  const renderWithSidebar = (mainContent: React.ReactNode) => (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        bgcolor: '#f5f5f5',
      }}
    >
      {/* Sidebar with call history */}
      <Box
        sx={{
          width: 320,
          bgcolor: 'white',
          borderRight: '1px solid #e0e0e0',
          p: 3,
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          {text.callHistory}
        </Typography>
        {CALL_HISTORY.map((call) => (
          <Box
            key={call.id}
            onClick={() => setSelectedCall(call)}
            sx={{
              mb: 2,
              p: 2,
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#e9ecef',
                transform: 'translateX(4px)',
              },
            }}
          >
            <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
              {call.topic}
            </Box>
            <Box sx={{ fontSize: '12px', color: '#666' }}>
              {call.date} · {text.callTime} {call.duration}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Main area */}
      {mainContent}
    </Box>
  );

  // Home screen - before call
  if (callState === CallState.INCOMING && !hasStartedCall) {
    return (
      <>
        {renderWithSidebar(
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: 500,
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto',
                  mb: 3,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                }}
              >
                <img
                  src={agentInfo.profile.avatar}
                  alt="HR Support"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </Box>

              {/* Title */}
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {agentInfo.profile.name}
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
                {text.subtitle}
              </Typography>

              {/* Start Call Button */}
              <Box
                onClick={handleStartCall}
                sx={{
                  px: 6,
                  py: 3,
                  bgcolor: '#E84749',
                  color: 'white',
                  borderRadius: 3,
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(232, 71, 73, 0.3)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#C73437',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(232, 71, 73, 0.4)',
                  },
                }}
              >
                {text.startButton}
              </Box>

              {/* 24/7 availability */}
              <Typography variant="caption" sx={{ mt: 3, color: '#666', display: 'block' }}>
                {text.availability}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Call Summary Dialog */}
        <Dialog
          open={!!selectedCall}
          onClose={() => setSelectedCall(null)}
          maxWidth="sm"
          fullWidth
        >
          {selectedCall && (
            <>
              <DialogTitle sx={{ m: 0, p: 2 }}>
                {selectedCall.topic}
                <IconButton
                  aria-label="close"
                  onClick={() => setSelectedCall(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  ✕
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  {selectedCall.date} · {text.callTime} {selectedCall.duration}
                </Typography>
                <Typography variant="body1">
                  {selectedCall.summary}
                </Typography>
              </DialogContent>
            </>
          )}
        </Dialog>
      </>
    );
  }

  // Connecting screen
  if (hasStartedCall && callState === CallState.INCOMING) {
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <img
              src={agentInfo.profile.avatar}
              alt="HR Support"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {text.connecting}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {[0, 0.5, 1].map((delay) => (
              <Box
                key={delay}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#E84749',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: `${delay}s`,
                  '@keyframes pulse': {
                    '0%, 60%': { opacity: 0.3 },
                    '30%': { opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Active call screen
  if (callState === CallState.ACTIVE) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 1400,
              margin: '0 auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={agentInfo.profile.avatar}
                  alt="HR Support"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ fontWeight: 600, color: '#333' }}>
                  {agentInfo.profile.name}
                </Box>
                <Box sx={{ fontSize: '14px', color: '#666' }}>
                  {company === 'grab' ? 'Live' : '通話中'} {formatTime(callDuration)}
                </Box>
              </Box>
            </Box>

            <Box
              onClick={handleEndCall}
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: '#DC3545',
                color: 'white',
                borderRadius: 2,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#B02A37',
                },
              }}
            >
              {text.endCall}
            </Box>
          </Box>
        </Box>

        {/* Main content with sidebar */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Call history sidebar */}
          <Box
            sx={{
              width: 320,
              bgcolor: 'white',
              borderRight: '1px solid #e0e0e0',
              p: 3,
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {text.callHistory}
            </Typography>
            {CALL_HISTORY.map((call) => (
              <Box
                key={call.id}
                onClick={() => setSelectedCall(call)}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: '#f8f9fa',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#e9ecef',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                  {call.topic}
                </Box>
                <Box sx={{ fontSize: '12px', color: '#666' }}>
                  {call.date} · {text.callTime} {call.duration}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Conversation area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <HRConversationArea
              transcript={transcript}
              isSpeaking={isSpeaking}
              company={company}
            />
          </Box>
        </Box>

        {/* Call Summary Dialog */}
        <Dialog
          open={!!selectedCall}
          onClose={() => setSelectedCall(null)}
          maxWidth="sm"
          fullWidth
        >
          {selectedCall && (
            <>
              <DialogTitle sx={{ m: 0, p: 2 }}>
                {selectedCall.topic}
                <IconButton
                  aria-label="close"
                  onClick={() => setSelectedCall(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  ✕
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  {selectedCall.date} · {text.callTime} {selectedCall.duration}
                </Typography>
                <Typography variant="body1">
                  {selectedCall.summary}
                </Typography>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    );
  }

  // Call ended screen
  if (callState === CallState.ENDED) {
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3 }}>✓</Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {text.callEnded}
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
          {text.callTime}: {formatTime(callDuration)}
        </Typography>

        <Box sx={{ textAlign: 'center' }}>
          <Box
            onClick={handleNewCall}
            sx={{
              px: 4,
              py: 2,
              bgcolor: '#E84749',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              mb: 2,
              '&:hover': {
                bgcolor: '#C73437',
              },
            }}
          >
            {text.newCall}
          </Box>

          <Typography variant="caption" sx={{ color: '#666' }}>
            {text.thankYou}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error screen
  if (error && loadingState === LoadingState.ERROR) {
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3 }}>⚠️</Box>
        <Typography variant="h6" sx={{ color: '#DC3545', mb: 3 }}>
          {text.error}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3, textAlign: 'center' }}>
          {text.errorMessage}
        </Typography>
        <Box
          onClick={handleNewCall}
          sx={{
            px: 4,
            py: 2,
            bgcolor: '#E84749',
            color: 'white',
            borderRadius: 2,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#C73437',
            },
          }}
        >
          {text.reconnect}
        </Box>
      </Box>
    );
  }

  return null;
}